import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { useAuthStore } from './stores/auth.store';
import { publicApi } from './api/client';
import { ThemeProvider, useTheme } from './context/ThemeContext';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import SettingsPage from './pages/SettingsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AdminPage from './pages/AdminPage';
import SetupWizardPage from './pages/SetupWizardPage';
import NotFoundPage from './pages/NotFoundPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 2 * 60 * 1000, // 2 minutes default cache
      refetchOnWindowFocus: false, // Disable to prevent rate limit issues
      refetchOnReconnect: false,
    },
  },
});

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const location = useLocation();
  
  // Check OAuth status for admins
  const { data: oauthStatus, isLoading: oauthLoading } = useQuery({
    queryKey: ['public-oauth-status'],
    queryFn: () => publicApi.getOAuthStatus(),
    enabled: isAuthenticated && (user?.role === 'admin' || user?.role === 'superadmin'),
    staleTime: 60 * 1000,
  });
  
  if (isLoading || (isAuthenticated && user?.role && ['admin', 'superadmin'].includes(user.role) && oauthLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Redirect admins to setup wizard if no OAuth is configured
  // But only if they're not already on the setup or admin page
  if (
    user?.role &&
    ['admin', 'superadmin'].includes(user.role) &&
    oauthStatus &&
    !oauthStatus.anyConfigured &&
    location.pathname !== '/setup' &&
    location.pathname !== '/admin'
  ) {
    return <Navigate to="/setup" replace />;
  }
  
  return <>{children}</>;
}

// Admin-only route wrapper
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const location = useLocation();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
}

// Public route (redirect to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
}

// Toaster with theme support
function ThemedToaster() {
  const { theme } = useTheme();
  
  return (
    <Toaster 
      theme={theme}
      position="bottom-right"
      toastOptions={{
        style: theme === 'dark' ? {
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-default)',
          color: 'var(--text-primary)',
        } : {
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-default)',
          color: 'var(--text-primary)',
        },
      }}
    />
  );
}

function AppRoutes() {
  const { checkAuth } = useAuthStore();
  
  useEffect(() => {
    checkAuth();
  }, []);
  
  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route 
          path="/" 
          element={
            <PublicRoute>
              <LandingPage />
            </PublicRoute>
          }
        />
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route 
          path="/register" 
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <AnalyticsPage />
            </ProtectedRoute>
          }
        />

        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="/setup"
          element={
            <AdminRoute>
              <SetupWizardPage />
            </AdminRoute>
          }
        />

        {/* Legal pages */}
        <Route path="/impressum" element={<LegalPage title="Impressum" />} />
        <Route path="/datenschutz" element={<LegalPage title="Datenschutz" />} />
        <Route path="/agb" element={<LegalPage title="AGB" />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      
      <ThemedToaster />
    </>
  );
}

// Simple legal page component
function LegalPage({ title }: { title: string }) {
  return (
    <div className="min-h-screen py-12 px-4 bg-page">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-h1 mb-8" style={{ color: 'var(--text-primary)' }}>{title}</h1>
        <div className="prose">
          <p style={{ color: 'var(--text-tertiary)' }}>
            [HIER EINFÜGEN: {title}-Text]
          </p>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AppRoutes />
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
